import { Box, Text } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';

import { GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT } from '../CustomDash/constants/panelWidths';
import { useGlobalAIAssistant } from './global-ai-assistant-context';

export function GlobalAIAssistantChartPreviewSlot({
  messageId,
  intersectionRoot,
}: {
  messageId: string;
  /** Transcript scroll container; when null, visibility uses the viewport. */
  intersectionRoot: HTMLElement | null;
}) {
  const {
    registerChartPreviewMount,
    setChartPreviewSlotViewportVisible,
    liveChartPreviewMessageIds,
    chartPreviewRuntimeByMessageId,
  } = useGlobalAIAssistant();

  const isLive = liveChartPreviewMessageIds.includes(messageId);
  const runtimePhase = chartPreviewRuntimeByMessageId[messageId]?.phase;
  const slotMinHeight =
    runtimePhase === 'success'
      ? 'min-content'
      : GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT;
  const elementRef = useRef<HTMLDivElement | null>(null);

  const setMountRef = useCallback(
    (node: HTMLDivElement | null) => {
      elementRef.current = node;
      registerChartPreviewMount(messageId, node);
    },
    [messageId, registerChartPreviewMount]
  );

  useEffect(() => {
    const el = elementRef.current;
    if (!el) {
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setChartPreviewSlotViewportVisible(messageId, entry.isIntersecting);
      },
      {
        root: intersectionRoot,
        threshold: 0,
        rootMargin: '0px 0px 160px 0px',
      }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
    };
  }, [messageId, intersectionRoot, setChartPreviewSlotViewportVisible]);

  return (
    <Box w="100%" flexShrink={0}>
      {!isLive ? (
        <Text fontSize="xs" color="gray.500" mb={2}>
          Chart preview idle — scroll this message into view to load.
        </Text>
      ) : null}
      <Box
        ref={setMountRef}
        minH={slotMinHeight}
        w="100%"
        flexShrink={0}
        data-testid={`global-ai-assistant-chart-slot-${messageId}`}
      />
    </Box>
  );
}
